/**
 * Generic Hero Section for City Landing Pages
 *
 * Fully parameterized hero component that accepts city-specific
 * content via props. Supports optional ScrollMagic animations.
 *
 * Usage:
 *   <HeroSection
 *     cityName="Houston"
 *     stateName="Texas"
 *     headline={{
 *       line1: "Your Voice, Amplified.",
 *       line2: "The Tools Houston Needs",
 *       line3: "to Organize and Be Heard."
 *     }}
 *     subheadline="A 4-3 vote shouldn't be the final word..."
 *     ctaText="Yes, I'm Interested"
 *     heroImage="/media_houston/hero.png"
 *     enableScrollAnimation={true}
 *   />
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

export interface HeroSectionProps {
  // City identity
  cityName: string;
  stateName: string;

  // Content
  badgeText?: string; // e.g., "Launching Soon in Houston"
  headline: {
    line1: string;
    line2: string;
    line3?: string;
  };
  subheadline: string;
  bodyText: string;

  // CTA buttons
  primaryCta: {
    text: string;
    action?: () => void; // Optional custom action
  };
  secondaryCta?: {
    text: string;
    action?: () => void;
  };

  // Hero image
  heroImage: {
    src: string;
    webpSrc?: string;
    alt: string;
    width?: number;
    height?: number;
  };

  // Animation controls
  enableScrollAnimation?: boolean; // Default: false (opt-in for ScrollMagic)
  animationIntensity?: 'subtle' | 'medium' | 'epic'; // Default: 'subtle'
}

export function HeroSection(props: HeroSectionProps) {
  const {
    cityName,
    stateName,
    badgeText,
    headline,
    subheadline,
    bodyText,
    primaryCta,
    secondaryCta,
    heroImage,
    enableScrollAnimation = false,
    animationIntensity = 'subtle',
  } = props;

  // Default scroll actions
  const scrollToForm = () => {
    if (primaryCta.action) {
      primaryCta.action();
    } else {
      const formElement = document.getElementById('interest-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const scrollToFeatures = () => {
    if (secondaryCta?.action) {
      secondaryCta.action();
    } else {
      const featuresElement = document.getElementById('features');
      if (featuresElement) {
        featuresElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // ScrollMagic animation (lazy-loaded, optional)
  React.useEffect(() => {
    if (!enableScrollAnimation) return;

    let controller: any;
    let pinScene: any;
    let photoScene: any;

    // Polyfill for passive event listeners
    const supportsPassive = (() => {
      let passive = false;
      try {
        const opts = Object.defineProperty({}, 'passive', {
          get() {
            passive = true;
            return passive;
          },
        });
        window.addEventListener('test', null as any, opts);
        window.removeEventListener('test', null as any, opts);
      } catch (e) {}
      return passive;
    })();

    // Monkey-patch addEventListener for passive wheel events
    if (supportsPassive && typeof window !== 'undefined') {
      const originalAddEventListener = EventTarget.prototype.addEventListener;
      EventTarget.prototype.addEventListener = function (
        type: string,
        listener: any,
        options?: any,
      ) {
        if (
          type === 'wheel' ||
          type === 'mousewheel' ||
          type === 'touchstart' ||
          type === 'touchmove'
        ) {
          const opts =
            typeof options === 'object'
              ? { ...options, passive: true }
              : { passive: true, capture: options };
          return originalAddEventListener.call(this, type, listener, opts);
        }
        return originalAddEventListener.call(this, type, listener, options);
      };
    }

    (async () => {
      try {
        const mod: any = await import('scrollmagic');
        const ScrollMagic = mod.default || mod;
        controller = new ScrollMagic.Controller();

        // Animation intensity multipliers
        const intensityConfig = {
          subtle: { zoom: 0.04, duration: '100%', textMove: 40 },
          medium: { zoom: 0.06, duration: '110%', textMove: 60 },
          epic: { zoom: 0.08, duration: '120%', textMove: 80 },
        };
        const config = intensityConfig[animationIntensity];

        // Get animated elements
        const heroSection = document.getElementById('hero-section');
        const heroPhoto = document.getElementById('hero-photo');
        const heroHeadline = document.getElementById('hero-headline');
        const heroSubhead = document.getElementById('hero-subhead');
        const heroSubtext = document.getElementById('hero-subtext');
        const heroCtas = document.getElementById('hero-ctas');
        const heroCredit = document.getElementById('hero-credit');

        // Pin scene
        if (heroSection) {
          pinScene = new ScrollMagic.Scene({
            triggerElement: '#hero-section',
            triggerHook: 0,
            duration: config.duration,
          })
            .setPin('#hero-section', { pushFollowers: true })
            .on('progress', (e: any) => {
              const progress = e.progress;

              // Photo zoom
              if (heroPhoto) {
                const scale = 1 + progress * config.zoom;
                heroPhoto.style.transform = `scale(${scale.toFixed(3)})`;
                heroPhoto.style.opacity = '1';
              }

              // Credit fade
              if (heroCredit) {
                const creditOpacity = Math.max(0, 1 - progress * 2);
                heroCredit.style.opacity = creditOpacity.toFixed(2);
              }

              // Headline fade + translate
              if (heroHeadline) {
                const headlineY = -progress * config.textMove;
                const headlineOpacity = Math.max(0, 1 - progress * 1.5);
                heroHeadline.style.transform = `translateY(${headlineY}px)`;
                heroHeadline.style.opacity = headlineOpacity.toFixed(2);
              }

              // Subhead fade + translate
              if (heroSubhead) {
                const subheadY = -progress * (config.textMove * 1.25);
                const subheadOpacity = Math.max(0, 1 - progress * 1.8);
                heroSubhead.style.transform = `translateY(${subheadY}px)`;
                heroSubhead.style.opacity = subheadOpacity.toFixed(2);
              }

              // Subtext fade + translate
              if (heroSubtext) {
                const subtextY = -progress * (config.textMove * 1.5);
                const subtextOpacity = Math.max(0, 1 - progress * 2);
                heroSubtext.style.transform = `translateY(${subtextY}px)`;
                heroSubtext.style.opacity = subtextOpacity.toFixed(2);
              }

              // CTAs fade + scale
              if (heroCtas) {
                const ctaScale = 1 - progress * 0.2;
                const ctaOpacity = Math.max(0, 1 - progress * 2.5);
                heroCtas.style.transform = `scale(${ctaScale.toFixed(3)})`;
                heroCtas.style.opacity = ctaOpacity.toFixed(2);
              }
            })
            .addTo(controller);
        }

        // Photo entrance scene
        if (heroPhoto) {
          photoScene = new ScrollMagic.Scene({
            triggerElement: '#hero-photo',
            triggerHook: 0.85,
            reverse: false,
          })
            .on('enter', () => {
              if (heroPhoto) heroPhoto.classList.add('in-view');
            })
            .addTo(controller);
        }
      } catch (err) {
        console.warn('ScrollMagic not available:', err);
      }
    })();

    return () => {
      try {
        if (photoScene && photoScene.destroy) photoScene.destroy(true);
        if (pinScene && pinScene.destroy) pinScene.destroy(true);
        if (controller && controller.destroy) controller.destroy(true);
      } catch (_) {}
    };
  }, [enableScrollAnimation, animationIntensity]);

  // Ensure hero image fades in when animations are disabled
  React.useEffect(() => {
    if (enableScrollAnimation) return;
    const heroPhoto = document.getElementById('hero-photo');
    heroPhoto?.classList.add('in-view');
  }, [enableScrollAnimation]);

  return (
    <section id="hero" className="relative py-12 sm:py-16 md:py-24 lg:py-32 overflow-hidden">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10 animate-gradient-slow" />

      {/* Floating geometric shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-24 h-24 sm:w-32 sm:h-32 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-32 h-32 sm:w-48 sm:h-48 bg-accent/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '2s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 w-28 h-28 sm:w-40 sm:h-40 bg-primary/3 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '4s' }}
        />
      </div>

      <div className="container relative px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          {badgeText && (
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6 bg-primary/10 border border-primary/20 rounded-full text-xs sm:text-sm font-medium text-primary animate-fade-in">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              {badgeText}
            </div>
          )}

          {/* Headline */}
          <h1
            id="hero-headline"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-4 sm:mb-6 will-change-transform px-2"
          >
            <span className="block animate-fade-in-up">{headline.line1}</span>
            <span
              className="block animate-fade-in-up text-primary"
              style={{ animationDelay: '0.1s' }}
            >
              {headline.line2}
            </span>
            {headline.line3 && (
              <span className="block animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                {headline.line3}
              </span>
            )}
          </h1>

          {/* Sub-headline */}
          <p
            id="hero-subhead"
            className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed animate-fade-in-up will-change-transform px-4"
            style={{ animationDelay: '0.3s' }}
          >
            {subheadline}
          </p>

          {/* Body text */}
          <p
            id="hero-subtext"
            className="text-lg sm:text-base md:text-lg text-foreground mb-8 sm:mb-10 max-w-2xl mx-auto font-medium animate-fade-in-up will-change-transform px-4"
            style={{ animationDelay: '0.4s' }}
          >
            {bodyText}
          </p>

          {/* Hero Image */}
          <div
            className="relative mt-6 sm:mt-8 mb-8 sm:mb-10 animate-fade-in-up px-2 sm:px-4"
            style={{ animationDelay: '0.45s' }}
          >
            <div className="relative overflow-hidden rounded-lg sm:rounded-xl shadow-xl sm:shadow-2xl aspect-square sm:aspect-video bg-gradient-to-br from-slate-50 to-slate-100">
              <img
                id="hero-photo"
                src={heroImage.src}
                alt={heroImage.alt}
                className="hero-photo w-full h-full mx-auto object-contain will-change-transform"
                loading="eager"
                width={heroImage.width || 800}
                height={heroImage.height || 800}
              />
            </div>
          </div>

          {/* CTA Buttons */}
          <div
            id="hero-ctas"
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center animate-fade-in-up will-change-transform px-4"
            style={{ animationDelay: '0.5s' }}
          >
            <Button
              size="lg"
              onClick={scrollToForm}
              className="w-full sm:w-auto text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            >
              {primaryCta.text}
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            {secondaryCta && (
              <Button
                size="lg"
                variant="outline"
                onClick={scrollToFeatures}
                className="w-full sm:w-auto text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 hover:bg-primary/5 transition-all duration-300 hover:scale-105"
              >
                {secondaryCta.text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
